import { SmartusComponentsModule } from '@affinis/smartus-components';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import {
  BaseSelectViewModel,
  ConfigurationTaskViewModel,
  DocumentTypeViewModel,
  GetDocumentTypesViewModel,
  IncidentClient,
  StorageClient,
  TaskDocumentTypeAssignmentViewModel,
  TaskDocumentTypeClient,
} from '@core/api';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { OverrideModule } from '@mandants/telekom/override/override.module';
import { MtxGridColumn } from '@ng-matero/extensions/grid';
import { FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  DataTableComponent,
  DEFAULT_TABLE_CONFIG,
  ITableConfig,
} from '@shared/components/data-table';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, finalize, Subject, takeUntil } from 'rxjs';
import { AddDocumentTypesComponent } from './add-document-types/add-document-types.component';

export interface ITaskDocumentTypeAssignmentViewModel
  extends TaskDocumentTypeAssignmentViewModel {
  default?: boolean;
}

@Component({
  selector: 'app-edit-document-types',
  imports: [
    CommonModule,
    SmartusComponentsModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyModule,
    OverrideModule,
    MatFormFieldModule,
    MatSelectModule,
    DataTableComponent,
    MatCheckboxModule,
    MatDialogModule,
  ],
  templateUrl: './edit-document-types.component.html',
  styleUrl: './edit-document-types.component.scss',
})
export class EditDocumentTypesComponent implements OnInit, OnDestroy {
  @ViewChild('checkboxTemplate', { static: true })
  checkboxTemplateRef!: TemplateRef<void>;

  @ViewChild('deleteTemplate', { static: true })
  deleteTemplateRef!: TemplateRef<void>;

  headline: string = this.translateService.instant(
    'menu.admin.component.edit_document_types'
  );

  form: FormGroup = this.fb.group({
    selectedTicketType: [''],
    selectedTicketClass: [{ value: '', disabled: true }],
    selectedTask: [{ value: '', disabled: true }],
  });

  private destroy$ = new Subject<void>();

  ticketTypes$ = new BehaviorSubject<string[]>([]);

  ticketClasses$ = new BehaviorSubject<BaseSelectViewModel[]>([]);

  tasks$ = new BehaviorSubject<ConfigurationTaskViewModel[]>([]);

  selectableDocTypes$ = new BehaviorSubject<DocumentTypeViewModel[]>([]);

  columns: MtxGridColumn[];

  config: ITableConfig = {
    ...DEFAULT_TABLE_CONFIG,
    rowSelectable: false,
    multiSelectable: false,
    filterFields: [
      {
        type: 'flex-layout',
        fieldGroup: [
          {
            key: 'documentTypeName',
            type: 'input',
            props: {
              label: this.translateService.instant(
                'edit_document_types.doc_type'
              ),
            },
          },
        ],
      },
    ],
  };

  public data$ = new BehaviorSubject<ITaskDocumentTypeAssignmentViewModel[]>(
    []
  );
  dataChange$ = this.data$.asObservable();

  typeControl = this.form.get('selectedTicketType');
  classControl = this.form.get('selectedTicketClass');
  taskControl = this.form.get('selectedTask');

  loading: boolean = false;
  loadingMessage: string = '';

  constructor(
    readonly breadcrumbService: BreadcrumbService,
    readonly translateService: TranslateService,
    readonly fb: FormBuilder,
    readonly incidentClient: IncidentClient,
    readonly storageClient: StorageClient,
    readonly dialog: MatDialog,
    readonly toastr: ToastrService,
    readonly taskDocumentTypeClient: TaskDocumentTypeClient,
    readonly cd: ChangeDetectorRef
  ) {
    this.breadcrumbService.setBreadcrumbs([{ label: this.headline }]);
  }

  ngOnInit(): void {
    this.incidentClient.getIncidentTypes().subscribe({
      next: incidentTypes => {
        const types = incidentTypes.filter(t => t !== 'Bau' && t !== 'OMV');
        this.ticketTypes$.next(types);
      },
      error: err => {
        console.log(err);
        this.toastr.error(
          this.translateService.instant('generic.errorMessage')
        );
      },
    });

    this.setupCascadingLogic();

    this.columns = [
      {
        header: this.translateService.instant('edit_document_types.doc_type'),
        field: 'documentTypeName',
        sortable: true,
      },
      {
        header: this.translateService.instant('edit_document_types.default'),
        field: 'default',
        sortable: true,
        cellTemplate: this.checkboxTemplateRef,
      },
      {
        header: this.translateService.instant('crud.delete'),
        field: 'delete',
        cellTemplate: this.deleteTemplateRef,
      },
    ];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupCascadingLogic(): void {
    if (!this.typeControl || !this.classControl || !this.taskControl) {
      return;
    }

    this.typeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(typeValue => {
        this.classControl.reset({ value: '', disabled: true });
        this.taskControl.reset({ value: '', disabled: true });
        this.data$.next([]);
        if (typeValue) {
          this.incidentClient
            .getIncidentClassesByIncidentType(typeValue)
            .subscribe({
              next: classes => {
                this.ticketClasses$.next(classes);
                if (classes.length > 0) {
                  this.classControl.enable();
                }
              },
              error: () => {
                this.toastr.error(
                  this.translateService.instant('generic.errorMessage')
                );
              },
            });
        }
      });

    this.classControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(classValue => {
        this.taskControl.reset({ value: '', disabled: true });
        this.data$.next([]);
        if (classValue && this.classControl.enabled) {
          this.incidentClient
            .getConfigurationTasksByIncidentClass(classValue)
            .subscribe({
              next: tasks => {
                this.tasks$.next(tasks);
                if (tasks.length > 0) {
                  this.taskControl.enable();
                }
              },
              error: () => {
                this.toastr.error(
                  this.translateService.instant('generic.errorMessage')
                );
              },
            });
        }
      });

    this.taskControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(taskValue => {
        if (taskValue) {
          this.getAssigned(taskValue);
        }
      });
  }

  getAssigned(taskValue) {
    this.taskDocumentTypeClient
      .getAssignedDocumentTypes(taskValue, undefined)
      .subscribe({
        next: res => {
          const assignmentsWithDefault = res.assignments.map(item => ({
            ...item,
            default: item.documentTypeId === res.defaultDocumentTypeId,
          }));

          this.data$.next(assignmentsWithDefault);
        },
        error: () => {
          this.toastr.error(
            this.translateService.instant('generic.errorMessage')
          );
        },
      });
  }

  openDialog() {
    const dialogRef = this.dialog.open(AddDocumentTypesComponent, {
      panelClass: 'smartportal-dialog',
      width: '800px',
      data: {
        title: this.translateService.instant(
          'edit_document_types.dialog_add.title'
        ),
        dismissButtonText: this.translateService.instant('shared.cancel'),
        okButtonText: this.translateService.instant('shared.add'),
        docTypes: this.selectableDocTypes$.value,
        docTypesCount: this.selectableDocTypes$.value.length,
      },
    });

    dialogRef.afterClosed().subscribe({
      next: docTypes => {
        if (docTypes) {
          const taskSequence = this.tasks$.value.find(
            t => t.id === this.taskControl.value
          )?.sequence;

          if (!taskSequence) {
            this.toastr.error(
              this.translateService.instant('generic.errorMessage')
            );
            return;
          }

          this.taskDocumentTypeClient
            .createAssignments(
              this.classControl.value,
              taskSequence,
              docTypes.map(d => d.documentTypeId)
            )
            .subscribe({
              next: () => {
                this.toastr.success(
                  this.translateService.instant(
                    'edit_document_types.success_add'
                  )
                );
                this.getAssigned(this.taskControl.value);
              },
              error: () => {
                this.toastr.error(
                  this.translateService.instant('generic.errorMessage')
                );
              },
            });
        }
      },
      error: () => {
        this.toastr.error(
          this.translateService.instant('generic.errorMessage')
        );
      },
    });
  }

  getDocumentTypesSelection() {
    this.loading = true;
    this.storageClient
      .getDocumentTypes(null)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (docTypesResult: GetDocumentTypesViewModel) => {
          const filteredDocTypes = docTypesResult.documentTypes.filter(
            t =>
              !this.data$.value.some(d => d.documentTypeId === t.documentTypeId)
          );
          this.selectableDocTypes$.next(filteredDocTypes);
          this.openDialog();
        },
        error: () => {
          this.toastr.error(
            this.translateService.instant('generic.errorMessage')
          );
        },
      });
  }

  onDeleteDocumentType(row: ITaskDocumentTypeAssignmentViewModel) {
    const deleteDialog = this.dialog.open(DialogComponent, {
      panelClass: 'smartportal-dialog',
      width: '500px',
      data: {
        type: 'okDismissButton',
        title: this.translateService.instant(
          'edit_document_types.dialog_delete.title'
        ),
        message: this.translateService.instant(
          'edit_document_types.dialog_delete.message'
        ),
        okButtonText: this.translateService.instant('shared.confirm'),
        dismissButtonText: this.translateService.instant('shared.cancel'),
      },
    });

    deleteDialog.afterClosed().subscribe({
      next: confirmed => {
        if (confirmed) {
          this.taskDocumentTypeClient
            .deleteAssignment(row.configurationTaskId, row.documentTypeId)
            .subscribe({
              next: () => {
                this.getAssigned(this.taskControl.value);
                this.toastr.success(
                  this.translateService.instant(
                    'edit_document_types.dialog_delete.success'
                  )
                );
              },
              error: () => {
                this.toastr.error(
                  this.translateService.instant(
                    'edit_document_types.dialog_delete.error'
                  )
                );
              },
            });
        }
      },
    });
  }

  onDefaultChange(row: ITaskDocumentTypeAssignmentViewModel) {
    this.cd.detectChanges();
    const currentData = this.data$.value;

    const index = currentData.findIndex(
      item => item.documentTypeId === row.documentTypeId
    );
    if (index === -1) {
      return;
    }

    if (row.default) {
      const alreadyHasDefault = this.checkIfDefaultExists(currentData, row);

      if (alreadyHasDefault) {
        row.default = false;
        this.toastr.error(
          this.translateService.instant('edit_document_types.error_default')
        );
        return;
      }
      this.taskDocumentTypeClient
        .setDefault(row.configurationTaskId, row.documentTypeId)
        .subscribe({
          next: () => {
            currentData[index].default = true;
          },
          error: () => {
            this.toastr.error(
              this.translateService.instant('generic.errorMessage')
            );
          },
        });
      currentData.forEach((item, i) => {
        if (i !== index && item.default) {
          item.default = false;
        }
      });
    } else {
      this.taskDocumentTypeClient
        .setDefault(row.configurationTaskId, null)
        .subscribe({
          next: () => {
            currentData[index].default = false;
          },
          error: () => {
            this.toastr.error(
              this.translateService.instant('generic.errorMessage')
            );
          },
        });
    }
  }

  checkIfDefaultExists(
    currentData: ITaskDocumentTypeAssignmentViewModel[],
    row: ITaskDocumentTypeAssignmentViewModel
  ): boolean {
    return currentData.some(
      item => item.default && item.documentTypeId !== row.documentTypeId
    );
  }

  assumeForAllTasks() {
    const deleteDialog = this.dialog.open(DialogComponent, {
      panelClass: 'smartportal-dialog',
      width: '500px',
      data: {
        type: 'okDismissButton',
        title: this.translateService.instant(
          'edit_document_types.dialog_take_over.title'
        ),
        message: this.translateService.instant(
          'edit_document_types.dialog_take_over.message'
        ),
        okButtonText: this.translateService.instant('shared.confirm'),
        dismissButtonText: this.translateService.instant('shared.cancel'),
      },
    });

    deleteDialog.afterClosed().subscribe({
      next: confirmed => {
        if (confirmed) {
          this.taskDocumentTypeClient
            .takeOverAssignments(this.taskControl.value)
            .subscribe({
              next: () => {
                this.toastr.success(
                  this.translateService.instant(
                    'edit_document_types.dialog_take_over.success'
                  )
                );
              },
              error: () => {
                this.toastr.error(
                  this.translateService.instant(
                    'edit_document_types.dialog_take_over.error'
                  )
                );
              },
            });
        }
      },
    });
  }
}
